import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Archive, Calendar, PackageX } from 'lucide-react';
import { api } from '@/lib/api/client';
import { stockApi, allocationsApi } from '@/features/stock/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  XkEvent,
  XkEventStockStation,
  XkStation,
  XkStock,
} from '@/lib/api/types';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';

/**
 * "Leftovers" tab for the brand profile.
 *
 * Lists past events (endDate < today) where the brand still has unused
 * units of one or more xotskies. For each event we group leftover units
 * by stock and surface the total available count.
 *
 * Data sources (all already cached by other surfaces):
 *   · queryKeys.events.byBrand        — every event of the brand
 *   · queryKeys.stations.byBrand      — every station of the brand
 *   · queryKeys.stockAllocations.byBrand — every active allocation
 *   · queryKeys.stock.byBrand         — for names + images of each stock
 */
export function LeftoversTab({ brandId }: { brandId: number | null }) {
  const t = useT();

  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkEvent>('/xk-event', { filter: { companyId: brandId }, perPage: 500 }),
    enabled: !!brandId,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId }, perPage: 500 }),
    enabled: !!brandId,
  });
  const allocsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byBrand(brandId ?? 0),
    queryFn: () => allocationsApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });
  const stocksQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId ?? 0),
    queryFn: () => stockApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });

  const rows = useMemo<LeftoverRow[]>(() => {
    if (
      !eventsQ.data ||
      !stationsQ.data ||
      !allocsQ.data ||
      !stocksQ.data
    ) {
      return [];
    }
    const now = Date.now();
    const stockById = new Map<number, XkStock>();
    for (const s of stocksQ.data.items) stockById.set(s.id, s);

    const stationsByEvent = new Map<number, XkStation[]>();
    for (const st of stationsQ.data.items) {
      const list = stationsByEvent.get(st.eventId) ?? [];
      list.push(st);
      stationsByEvent.set(st.eventId, list);
    }
    const allocsByStation = new Map<number, XkEventStockStation[]>();
    for (const a of allocsQ.data.items) {
      const list = allocsByStation.get(a.stationId) ?? [];
      list.push(a);
      allocsByStation.set(a.stationId, list);
    }

    const pastEvents = eventsQ.data.items.filter((e) => {
      const end = e.endDate ? new Date(e.endDate).getTime() : null;
      return end != null && end < now;
    });

    const out: LeftoverRow[] = [];
    for (const e of pastEvents) {
      const stations = stationsByEvent.get(e.id) ?? [];
      // Aggregate leftover units per stock across all stations of the event.
      const leftoverByStock = new Map<number, number>();
      for (const st of stations) {
        const allocs = allocsByStation.get(st.id) ?? [];
        for (const a of allocs) {
          if ((a.available ?? 0) > 0) {
            leftoverByStock.set(
              a.stockId,
              (leftoverByStock.get(a.stockId) ?? 0) + (a.available ?? 0),
            );
          }
        }
      }
      if (leftoverByStock.size === 0) continue;
      const items: LeftoverStockItem[] = [];
      let totalUnits = 0;
      for (const [stockId, qty] of leftoverByStock) {
        if (qty <= 0) continue;
        const stock = stockById.get(stockId);
        if (!stock) continue;
        totalUnits += qty;
        items.push({
          stockId,
          name: stock.name,
          image: stock.image ?? stock.logo ?? null,
          rating: stock.rating ?? 3,
          quantity: qty,
        });
      }
      // Hide events that have no displayable leftover items — happens
      // when every contributing stock was disabled/deleted, or when the
      // aggregated quantities resolved to 0 after filtering.
      if (items.length === 0 || totalUnits <= 0) continue;
      items.sort((a, b) => b.quantity - a.quantity);
      out.push({
        eventId: e.id,
        eventGroupId: e.eventGroupId,
        eventName: e.name,
        endDate: e.endDate ?? null,
        items,
        totalUnits,
      });
    }
    out.sort((a, b) => {
      const ad = a.endDate ? new Date(a.endDate).getTime() : 0;
      const bd = b.endDate ? new Date(b.endDate).getTime() : 0;
      return bd - ad;
    });
    return out;
  }, [eventsQ.data, stationsQ.data, allocsQ.data, stocksQ.data]);

  if (!brandId) {
    return (
      <EmptyState
        icon={<PackageX className="h-6 w-6" />}
        title={t('brand.leftovers.noBrand')}
        description={t('brand.leftovers.noBrandBody')}
      />
    );
  }

  const loading =
    eventsQ.isLoading ||
    stationsQ.isLoading ||
    allocsQ.isLoading ||
    stocksQ.isLoading;

  return (
    <div className="space-y-4">
      <header>
        <h3
          className="text-base font-black tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {t('brand.leftovers.title')}
        </h3>
        <p className="mt-0.5 text-xs text-[var(--color-xk-text-muted)]">
          {t('brand.leftovers.subtitle')}
        </p>
      </header>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<PackageX className="h-6 w-6" />}
          title={t('brand.leftovers.emptyTitle')}
          description={t('brand.leftovers.emptyBody')}
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <LeftoverRowCard key={row.eventId} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface LeftoverStockItem {
  stockId: number;
  name: string;
  image: string | null;
  rating: number;
  quantity: number;
}

interface LeftoverRow {
  eventId: number;
  eventGroupId: number;
  eventName: string;
  endDate: string | null;
  items: LeftoverStockItem[];
  totalUnits: number;
}

function LeftoverRowCard({ row }: { row: LeftoverRow }) {
  const t = useT();
  return (
    <li className="overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)]">
      <header className="flex items-start justify-between gap-3 border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-4 py-3">
        <div className="min-w-0">
          <div
            className="truncate text-[14px] font-extrabold text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {row.eventName}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[var(--color-xk-text-muted)]">
            <Calendar className="h-3 w-3" />
            {row.endDate ? dayjs(row.endDate).format('MMM D, YYYY') : '—'}
          </div>
        </div>
        <div className="flex-none text-right">
          <div
            className="text-[18px] font-black text-[var(--color-accent-ink)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {row.totalUnits}
          </div>
          <div className="text-[9px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            {t('brand.leftovers.totalLeftover')}
          </div>
        </div>
        {/* El cierre vive en el informe del evento: allí se cuenta, se devuelve y queda el acta. */}
        <Link
          to={`/projects/${row.eventGroupId}/events/${row.eventId}?tab=measure`}
          className="inline-flex flex-none items-center gap-1.5 rounded-full border border-[var(--color-accent-ink)] px-3 py-1.5 text-xs font-bold text-[var(--color-accent-ink)] hover:bg-[var(--color-accent-06)]"
        >
          <Archive className="h-3.5 w-3.5" />
          {t('brand.leftovers.close')}
        </Link>
      </header>
      <ul className="divide-y divide-[var(--color-xk-border)]">
        {row.items.map((it) => (
          <li
            key={it.stockId}
            className="flex items-center gap-3 px-4 py-2.5"
          >
            <div className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded bg-[var(--color-xk-bg-deep)]">
              {it.image ? (
                <img
                  src={resolveImageUrl(it.image)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <PackageX className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-[var(--color-xk-lavender)]">
                {it.name}
              </div>
              <div className="text-[10px] text-[var(--color-xk-text-muted)]">
                <Stars value={it.rating} />
              </div>
            </div>
            <div className="text-right">
              <div
                className="text-[15px] font-extrabold text-[var(--color-xk-lavender)] tabular-nums"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {it.quantity}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[var(--color-xk-text-muted)]">
                {t('brand.leftovers.units')}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </li>
  );
}

function Stars({ value }: { value: number }) {
  const v = Math.max(1, Math.min(5, value));
  return (
    <span aria-label={`Rating ${v}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={
            i <= v
              ? 'text-[var(--color-accent-ink)]'
              : 'text-[var(--color-xk-border)]'
          }
        >
          ★
        </span>
      ))}
    </span>
  );
}
